import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import './ContractDateInput.css'
const { validCivilDate, dateOnlyInBangkok } = require('../utils/contractDates')
const months = ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม']
const pad = n => String(n).padStart(2, '0')
const isoDate = (year, month, day) => `${String(year).padStart(4, '0')}-${pad(month + 1)}-${pad(day)}`

export function formatContractDate(iso) {
  if (!validCivilDate(iso)) return ''
  const [year, month, day] = iso.split('-')
  return `${day}/${month}/${Number(year) + 543}`
}
export function parseContractDate(text) {
  const normalized = String(text || '').replace(/[๐-๙]/g, char => String(char.charCodeAt(0) - 0x0e50))
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(normalized)
  if (!match) return ''
  const [, day, month, buddhistYear] = match
  const year = Number(buddhistYear) - 543
  const iso = `${String(year).padStart(4, '0')}-${month}-${day}`
  return validCivilDate(iso) ? iso : ''
}

export default function ContractDateInput({ value, onChange, required = true, ...props }) {
  const [text, setText] = useState(formatContractDate(value))
  const [open, setOpen] = useState(false)
  const initial = new Date(`${validCivilDate(value) ? value : dateOnlyInBangkok(new Date())}T00:00:00Z`)
  const [view, setView] = useState({ year: initial.getUTCFullYear(), month: initial.getUTCMonth() })
  const [yearDraft, setYearDraft] = useState(String(initial.getUTCFullYear() + 543))
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const input = useRef(null)
  const trigger = useRef(null)
  const dialog = useRef(null)
  const editing = useRef(false)
  const label = props['aria-label'] || 'วันที่สัญญา'
  useEffect(() => { setYearDraft(String(view.year + 543)) }, [view.year])
  const commitYear = () => {
    const year = Number(yearDraft) - 543
    if (Number.isInteger(year) && year >= 1900 && year <= 9456) setView({ ...view, year })
    else setYearDraft(String(view.year + 543))
  }
  useEffect(() => {
    if (!value && editing.current) { editing.current = false; return }
    setText(formatContractDate(value))
    input.current?.setCustomValidity('')
    editing.current = false
  }, [value])
  useEffect(() => {
    if (!open) return
    const outside = event => {
      if (!dialog.current?.contains(event.target) && !trigger.current?.contains(event.target)) setOpen(false)
    }
    const close = event => {
      if (event.key === 'Escape') { setOpen(false); trigger.current?.focus() }
    }
    const reposition = () => {
      const rect = trigger.current?.getBoundingClientRect()
      if (!rect) return
      const width = Math.min(340, window.innerWidth - 24)
      setPosition({ left: Math.max(12, Math.min(rect.right - width, window.innerWidth - width - 12)), top: Math.max(12, Math.min(rect.bottom + 8, window.innerHeight - 410)) })
    }
    reposition()
    dialog.current?.querySelector('select')?.focus()
    document.addEventListener('pointerdown', outside)
    document.addEventListener('keydown', close)
    window.addEventListener('resize', reposition)
    return () => {
      document.removeEventListener('pointerdown', outside)
      document.removeEventListener('keydown', close)
      window.removeEventListener('resize', reposition)
    }
  }, [open])
  useEffect(() => { if (props.disabled) setOpen(false) }, [props.disabled])
  const choose = iso => {
    setText(formatContractDate(iso))
    input.current?.setCustomValidity('')
    editing.current = false
    onChange(iso)
    setOpen(false)
    trigger.current?.focus()
  }
  const moveMonth = offset => {
    const date = new Date(Date.UTC(view.year, view.month + offset, 1))
    if (date.getUTCFullYear() >= 1900 && date.getUTCFullYear() <= 9456) setView({ year: date.getUTCFullYear(), month: date.getUTCMonth() })
  }
  const firstDay = new Date(Date.UTC(view.year, view.month, 1)).getUTCDay()
  const dayCount = new Date(Date.UTC(view.year, view.month + 1, 0)).getUTCDate()
  const todayParts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Bangkok', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date())
  const today = Object.fromEntries(todayParts.map(item => [item.type, item.value]))
  const todayIso = `${today.year}-${today.month}-${today.day}`
  return <span className="contract-date-control">
    <input {...props} ref={input} type="text" inputMode="numeric" placeholder="วว/ดด/ปปปป พ.ศ." maxLength={10} required={required}
      value={text} onChange={event => {
        const raw = event.target.value
        setText(raw)
        const iso = parseContractDate(raw)
        event.target.setCustomValidity(raw && !iso ? 'กรุณาเลือกวันจากปฏิทิน หรือกรอกวันที่ พ.ศ. เช่น 01/10/2569' : '')
        editing.current = true
        onChange(iso)
      }} />
    <button ref={trigger} className="contract-date-trigger" type="button" disabled={props.disabled}
      aria-label={`${label} เลือกจากปฏิทิน`} aria-expanded={open} aria-haspopup="dialog"
      onClick={() => {
        if (validCivilDate(value)) { const date = new Date(`${value}T00:00:00Z`); setView({ year: date.getUTCFullYear(), month: date.getUTCMonth() }) }
        setOpen(!open)
      }}>
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><rect x="3" y="5" width="18" height="16" rx="3"/><path d="M7 3v4M17 3v4M3 11h18M7 15h2M11 15h2M15 15h2"/></svg>
    </button>
    {open && createPortal(<div ref={dialog} role="dialog" aria-label={`ปฏิทิน${label} พ.ศ.`} className="contract-date-calendar" style={position}>
      <div className="contract-calendar-title"><strong>เลือก{label}</strong><button type="button" aria-label="ปิดปฏิทิน" onClick={() => { setOpen(false); trigger.current?.focus() }}>×</button></div>
      <div className="contract-calendar-nav">
        <button type="button" aria-label="เดือนก่อนหน้า" onClick={() => moveMonth(-1)}>‹</button>
        <select aria-label="เดือน" value={view.month} onChange={event => setView({ ...view, month: Number(event.target.value) })}>{months.map((month, index) => <option key={month} value={index}>{month}</option>)}</select>
        <label>พ.ศ.<input type="number" aria-label="ปี พ.ศ." min="2443" max="9999" value={yearDraft} onChange={event => setYearDraft(event.target.value)} onBlur={commitYear} onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); commitYear() } }} /></label>
        <button type="button" aria-label="เดือนถัดไป" onClick={() => moveMonth(1)}>›</button>
      </div>
      <div className="contract-calendar-grid">
        {['อา.', 'จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.'].map(day => <span className="contract-calendar-weekday" key={day}>{day}</span>)}
        {Array.from({ length: firstDay }, (_, index) => <span key={`empty-${index}`} />)}
        {Array.from({ length: dayCount }, (_, index) => {
          const day = index + 1
          const iso = isoDate(view.year, view.month, day)
          return <button type="button" key={day} aria-label={formatContractDate(iso)} aria-pressed={value === iso} aria-current={todayIso === iso ? 'date' : undefined}
            onClick={() => choose(iso)}>{day}</button>
        })}
      </div>
      <div className="contract-calendar-footer"><span>{months[view.month]} {view.year + 543}</span><button type="button" onClick={() => choose(todayIso)}>วันนี้</button></div>
    </div>, document.body)}
  </span>
}
